import React, { Fragment, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import deltIcon from "assets/images/delete.svg"
import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MyButton from "component/shared-component/form/MyButton";
import { bulkUploadValidation } from "helpers/ValidationHelper";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "component/shared-component/snackbar";
import MyTextField from "component/shared-component/form/MyTextField";
import MyPagination from "component/shared-component/form/MyPagination";
import { useUpdateBulkUpload } from "hooks/cofiguration/product/product";
import ConfigHeader from "component/shared-component/headers/configHeader";

const BulkUpload = () => {
    let location = useLocation();
    let navigate = useNavigate();

    const { showSnackbar } = useSnackbar();

    const [itemsPerPage] = useState(10);
    const [bulkData, setBulkData] = useState([]);
    const [hasDuplicate, setHasDuplicate] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [fileName, setFileName] = useState('');

    const { control, handleSubmit, reset, formState: { errors }, setValue, trigger } = useForm({
        resolver: yupResolver(bulkUploadValidation),
        defaultValues: { products: location.state?.data || location.state || [] }
    });


    const { mutate: updateBulkUpload } = useUpdateBulkUpload();

    const handleFieldChange = (index, field, value) => {
        setValue(`products[${index}].${field}`, value, { shouldValidate: true });
        trigger();
        const error = errors.products?.[index]?.[field];
        if (error) {
            updateBulkDataError(index, field, error.message);
        } else {
            updateBulkDataError(index, field, '');
        }
        if (field === 'badgeMacNumber') {
            const updatedData = [...bulkData];
            updatedData[index].badgeMacNumber = value;
            checkForDuplicateBadgeMacNumbers(updatedData);
        }
    };

    const updateBulkDataError = (index, field, message) => {
        setBulkData(prevData => {
            const updatedData = [...prevData];
            updatedData[index] = {
                ...updatedData[index],
                [`${field}Message`]: message
            };
            return updatedData;
        });
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = bulkData.slice(indexOfFirstItem, indexOfLastItem);


    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleUpdateProduct = (values) => {
        console.log(values);

        updateBulkUpload(JSON.stringify(values), {
            onSuccess: (item) => {
                if (item.statusCode === 200) {
                    showSnackbar(item?.message, 'success');
                    navigate("/configuration/product");
                    reset();
                } else {
                    showSnackbar(item?.message, 'error');
                }
            },
            onError: (error) => {
                showSnackbar(error?.message, 'error');
            }
        });
    }

    const onSubmit = (values) => {
        const clearErrorMessages = removeErrorMessages(values.products);
        handleUpdateProduct(clearErrorMessages);
    }

    const removeErrorMessages = (data) => {
        return data.map(item => {
            const {
                productIdMessage,
                productNameMessage,
                categoryNameMessage,
                locationMessage,
                badgeMacNumberMessage,
                productPriceMessage,
                ...removedItem
            } = item;
            return removedItem;
        });
    };

    const checkForDuplicateBadgeMacNumbers = (data) => {
        const badgeMacNumberMap = new Map();
        let duplicateFound = false; // Flag to track if any duplicates are found

        const updatedData = data.map((item, index) => {
            const badgeMacNumberLower = item.badgeMacNumber.toLowerCase(); // Normalize to lowercase

            // Check for duplicates in badgeMacNumber (case-insensitive)
            if (badgeMacNumberMap.has(badgeMacNumberLower)) {
                // Set the error message if duplicate is found
                item.badgeMacNumberMessage = 'Duplicate RFID Tag Number';
                duplicateFound = true; // Mark that a duplicate is found
            } else {
                // Otherwise, add it to the map
                badgeMacNumberMap.set(badgeMacNumberLower, index);
                item.badgeMacNumberMessage = null;
            }
            return item;
        });
        setBulkData(updatedData);
        setHasDuplicate(duplicateFound);
    };

    const handleDeleteRow = (indexToDelete) => {
        const actualIndex = indexToDelete + indexOfFirstItem;
        const updatedData = bulkData.filter((_, index) => index !== actualIndex);
        setBulkData(updatedData);
        
        // Update the form values as well
        const currentFormValues = control._formValues;
        if (currentFormValues && currentFormValues.products) {
            const updatedProducts = currentFormValues.products.filter((_, index) => index !== actualIndex);
            reset({ products: updatedProducts });
        }
        
        // Check for duplicates after deletion
        checkForDuplicateBadgeMacNumbers(updatedData);
    };


    useEffect(() => {
        const data = location.state?.data || location.state;
        const file = location.state?.fileName || '';
        
        if (data && data.length !== 0) {
            setBulkData(data);
            setFileName(file);
            reset({ products: data });
            checkForDuplicateBadgeMacNumbers(data);
        }
    }, [location.state, reset]);


    useEffect(() => {
        if (currentPage > Math.ceil(bulkData.length / itemsPerPage)) {
            setCurrentPage(1);
        }
    }, [bulkData, currentPage, itemsPerPage]);


    return (
        <Fragment>
            <ConfigHeader />
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="config-create-main">
                    <div className="inner-header createEdit-header">
                        <div className="title">
                            <Link to={`/configuration/product`}>
                                <div className="nav-back">
                                    <ArrowBackIcon />
                                    <span>{`Back to Product`}</span>
                                </div>
                            </Link>
                            <h1>Bulk Upload</h1>
                            <p>Add multiple products at once.</p>
                        </div>
                        <div className="txt-right single-line">
                            {fileName && (
                                <div style={{ 
                                    marginBottom: '8px',
                                    textAlign: 'right'
                                }}>
                                    <span style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '500', 
                                        color: '#495057',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        File Name: 📄 {fileName}
                                    </span>
                                </div>
                            )}
                            <MyButton
                                variant="contained"
                                color="primary"
                                size="medium"
                                fontSize="14px"
                                type="submit"
                                disabled={hasDuplicate}
                                style={{ width: 'auto', minWidth: 'auto' }}
                            >Verify & Update
                            </MyButton>
                        </div>
                    </div>
                    <br />
                </div>
                <div className='common-table bulk-upload-table'>
                    <table>
                        <thead>
                            <tr>
                                <th>Sr. No</th>
                                <th>Product Name</th>
                                <th>Asset Number</th>
                                <th>Category Name</th>
                                <th>Location</th>
                                <th>RFID Tag</th>     
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((item, index) => (
                                <tr key={index + indexOfFirstItem}>
                                    <td > <div style={{
                                        boxShadow: "rgba(9, 30, 66, 0.25) 0px 0px 1px, rgba(9, 30, 66, 0.13) 0px 0px 0px 1px",
                                        borderRadius: "6px",
                                        padding: "8px 12px",
                                        minHeight: "44px",
                                        display: "flex",
                                        alignItems: "center",
                                        lineHeight: '24px',
                                        width: '120px'
                                        
                                    }}>{index + indexOfFirstItem + 1}</div></td>
                                    <td>
                                        <MyTextField
                                            name={`products[${index + indexOfFirstItem}].productName`}
                                            control={control}
                                            rules={{ required: 'Enter product name' }}
                                            label=""
                                            autoComplete="off"
                                            error={!!errors.products?.[index]?.productName}
                                            errorMessage={errors.products?.[index]?.productName?.message || item.productNameMessage}
                                            placeholder="Enter product name"
                                            onChange={(e) => handleFieldChange(index, 'productName', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <MyTextField
                                            name={`products[${index + indexOfFirstItem}].assetNumber`}
                                            control={control}
                                            rules={{ required: 'Enter asset number' }}
                                            label=""
                                            autoComplete="off"
                                            error={!!errors.products?.[index + indexOfFirstItem]?.assetNumber}
                                            errorMessage={errors.products?.[index + indexOfFirstItem]?.assetNumber?.message || item.assetNumberMessage}
                                            placeholder="Enter asset number"
                                            onChange={(e) => handleFieldChange(index + indexOfFirstItem, 'assetNumber', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <MyTextField
                                            name={`products[${index + indexOfFirstItem}].categoryName`}
                                            control={control}
                                            rules={{ required: 'Enter category Name' }}
                                            label=""
                                            autoComplete="off"
                                            error={!!errors.products?.[index + indexOfFirstItem]?.categoryName}
                                            errorMessage={errors.products?.[index + indexOfFirstItem]?.categoryName?.message || item.categoryNameMessage}
                                            placeholder="Enter category Name"
                                            onChange={(e) => handleFieldChange(index + indexOfFirstItem, 'categoryName', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <MyTextField
                                            name={`products[${index + indexOfFirstItem}].location`}
                                            control={control}
                                            rules={{ required: 'Enter location name' }}
                                            label=""
                                            autoComplete="off"
                                            error={!!errors.products?.[index + indexOfFirstItem]?.location}
                                            errorMessage={errors.products?.[index + indexOfFirstItem]?.location?.message || item.locationMessage}
                                            placeholder="Enter location name"
                                            onChange={(e) => handleFieldChange(index + indexOfFirstItem, 'location', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <MyTextField
                                            name={`products[${index + indexOfFirstItem}].badgeMacNumber`}
                                            control={control}
                                            rules={{ required: 'Enter RFID Tag Number' }}
                                            label=""
                                            autoComplete="off"
                                            error={!!errors.products?.[index + indexOfFirstItem]?.badgeMacNumber}
                                            errorMessage={errors.products?.[index + indexOfFirstItem]?.badgeMacNumber?.message || item.badgeMacNumberMessage}
                                            placeholder="Enter RFID Tag Number"
                                            onChange={(e) => handleFieldChange(index + indexOfFirstItem, 'badgeMacNumber', e.target.value)}
                                        />
                                    </td>
                                   
                                    <td>
                                      <img 
                                        src={deltIcon} 
                                        alt="delete" 
                                        className="table-edit" 
                                        onClick={() => handleDeleteRow(index)}
                                        style={{ cursor: 'pointer' , marginLeft: '10px'}}
                                      />
                                    </td>
                                   
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </form>
            <div className="common-pagination">
                <MyPagination
                    count={Math.ceil(bulkData.length / itemsPerPage)}
                    page={currentPage}
                    onPageChange={handlePageChange}
                    siblingCount={1}
                    boundaryCount={1}
                    variant=""
                    shape="rounded"
                />
            </div>
        </Fragment>
    );
};

export default BulkUpload;
